import { useState } from 'react';
import { getMultisigBalanceHistory } from 'api';
import useGetTokenMetadata from 'hooks/use-getTokenMetadata';
import useNetwork from 'hooks/use-network';
import { SafeFtBalance } from 'store';
import { ChartData, HistoryPeriod, StxHistory } from 'types';

const tabs = {
  week: '7d',
  month: '30d',
} as const;

export type PeriodTabKey = keyof typeof tabs;

export type PeriodTabName = (typeof tabs)[keyof typeof tabs];

export type FormatedSafeTotalBalance = {
  balance: number;
  date?: string;
};

const assets = ['Inscriptions', 'RareSats', 'Runes', 'BRC20'] as const;

type FtBalanceWithPrice = SafeFtBalance & { price: number };

export const useDashboardPage = () => {
  const [selectedTabKey, setSelectedTabKey] = useState<PeriodTabKey>('week');
  const [selectedAsset, setSelectedAsset] = useState('Inscriptions');
  const [network] = useNetwork();
  const [isAssetLoading, setAssetLoading] = useState(false);

  const [allBalanceHistory, setAllBalanceHistory] = useState<ChartData[]>([]);
  const { fetchTokenMetadata } = useGetTokenMetadata();

  async function getFormatData(data: StxHistory[], stxPrice: number) {
    const totalData: ChartData[] = [];

    for (const item of data) {
      const tmpDate = item.date ? new Date(item.date) : new Date();
      const date =
        tmpDate.getDate().toString().padStart(2, '0') +
        '.' +
        (tmpDate.getMonth() + 1).toString().padStart(2, '0');

      const fungible_tokens = item.balance.fungible_tokens || [];
      const safeStxBalance = item.balance.stx;

      const stxBalance = {
        asset: {
          address: 'STX',
          symbol: 'STX',
          decimals: 6,
          ref: 'STX',
        },
        balance: Number(safeStxBalance.balance).toString(),
        price: stxPrice,
      };

      const ftBalancesApi: FtBalanceWithPrice[] = await Promise.all(
        fungible_tokens.map((ftToken) => {
          const [fullTokenAddress, tokenRef] = ftToken.token.split('::');

          return fetchTokenMetadata(fullTokenAddress, tokenRef);
        })
      ).then((metadataResponses) =>
        metadataResponses.map(
          ({ address, symbol, decimals, ref, metadata }, i): FtBalanceWithPrice => ({
            asset: { address, symbol, decimals, ref, metadata },
            balance: fungible_tokens[i].balance || '',
            price: fungible_tokens[i].price as number,
          })
        )
      );

      const ftBalances: FtBalanceWithPrice[] = [stxBalance, ...ftBalancesApi];

      const amount = ftBalances.reduce((acc, balanceItem) => {
        const price = balanceItem.price;
        const balance = Number(balanceItem.balance) / Math.pow(10, balanceItem.asset.decimals);
        if (!price) return acc;

        return acc + price * balance;
      }, 0);

      totalData.unshift({
        date,
        amount: Number(amount.toFixed(2)),
      });
    }

    return { totalData };
  }

  const loadHistory = (
    address: string,
    period: HistoryPeriod,
    stxPrice: number
  ): Promise<boolean> => {
    if (!address) return Promise.resolve(false);
    setAssetLoading(true);

    return getMultisigBalanceHistory(address, `stx:${network}`, period)
      .then(async (result) => {
        const parsedData = await getFormatData(result.result, stxPrice);
        setAllBalanceHistory(parsedData.totalData);

        return true;
      })
      .catch((err) => {
        console.error('Error loading assets history ', err);

        return false;
      })
      .finally(() => setAssetLoading(false));
  };

  return {
    tabs,
    selectedTabKey,
    setSelectedTabKey,
    assets,
    selectedAsset,
    setSelectedAsset,
    isAssetLoading,
    loadHistory,
    allBalanceHistory,
  };
};
